import React, { useState, useEffect } from "react";
import { Lightbulb, Send, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

export const FeedbackCampaign = ({ raised = false, hideLauncher = false }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("zanitel-feedback-open", h);
    return () => window.removeEventListener("zanitel-feedback-open", h);
  }, []);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Please tell us your name.");
    if (!email.trim() || !email.includes("@")) return toast.error("Please enter a valid email address.");
    if (!message.trim()) return toast.error("Please write a short comment first.");
    setSending(true);
    try {
      await api.post("/feedback", { name, email, message });
      toast.success("Thank you! Your feedback goes straight to our team. 💙");
      setName("");
      setEmail("");
      setMessage("");
      setOpen(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!hideLauncher && (
        <button
          onClick={() => setOpen(true)}
          data-testid="feedback-campaign-btn"
          className={`feedback-blink fixed left-4 z-[60] inline-flex items-center justify-center gap-2 h-12 w-12 sm:h-auto sm:w-auto sm:pl-3 sm:pr-4 sm:py-2.5 rounded-full bg-[#E11D48] text-white text-xs sm:text-sm font-bold ${raised ? "bottom-20 md:bottom-5" : "bottom-5"}`}
        >
          <Lightbulb className="h-5 w-5 sm:h-4 sm:w-4" />
          <span className="hidden sm:inline">How can we make your Zeep experience better?</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="feedback-modal">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 relative">
            <button
              onClick={() => setOpen(false)}
              data-testid="feedback-close"
              aria-label="Close feedback"
              className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-9 w-9 rounded-full bg-[#E11D48] flex items-center justify-center shrink-0">
                <Lightbulb className="h-4 w-4 text-white" />
              </div>
              <h3 className="font-heading font-bold text-lg leading-tight">How can we make your Zeep experience better?</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">Leave your details and comment — it goes straight to our support team.</p>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              data-testid="feedback-name-input"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E11D48]/40"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              data-testid="feedback-email-input"
              className="w-full mt-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E11D48]/40"
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Your comment — big or small, we read everything…"
              data-testid="feedback-message-input"
              className="w-full mt-2 rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E11D48]/40 resize-none"
            />
            <button
              onClick={submit}
              disabled={sending}
              data-testid="feedback-submit-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E11D48] text-white font-bold text-sm hover:bg-[#BE123C] transition-colors disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to our team
            </button>
            <p className="text-[11px] text-slate-400 text-center mt-3">Escalated directly to support@zeeptel.com — every message is read.</p>
          </div>
        </div>
      )}
    </>
  );
};
